import { Injectable } from '@angular/core';

import { IdentificationTypes } from 'app/core/enums';
import { ValidatorService } from 'app/validator.service';

@Injectable()
export class ValidatorPerson {
  private readonly WEIGHTS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  private readonly WEIGHTS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  constructor(public readonly validatorService: ValidatorService) {}

  validate(dni: string, identificationType: string): boolean {
    switch (identificationType) {
      case IdentificationTypes.CB_IDENTIFICACION_RUT:
        return this.validateRut(dni);
      case IdentificationTypes.CB_IDENTIFICACION_RUC:
        return this.validateDocumentPe(dni, 11);
      case IdentificationTypes.CB_IDENTIFICACION_DNI:
        return this.validateDocumentPe(dni, 8);
      case IdentificationTypes.CB_IDENTIFICACION_RFCPF:
        return this.validateRfcPF(dni);
      case IdentificationTypes.CB_IDENTIFICACION_RFCPM:
        return this.validateRfcPM(dni);
      case IdentificationTypes.CB_IDENTIFICACION_CPF:
        return this.validateCpf(dni);
      case IdentificationTypes.CB_IDENTIFICACION_CNPJ:
        return this.validateCnpj(dni);
      case IdentificationTypes.CB_IDENTIFICACION_OTHER:
        return !!dni;
      default:
        return false;
    }
  }

  /**
   * Valida el formato y el último caracter del RUT
   *
   * @param {string} dni
   * @return {*}  {boolean}
   * @memberof ValidatorPerson
   */
  validateRut(dni: string): boolean {
    const regex = /^[0-9]{7,}[0-9kK]$/;
    if (!dni || !regex.test(dni)) return false;
    return this.validatorService.validateRutLastDigit(dni);
  }

  validateDocumentPe(dni: string, digits: number): boolean {
    const regex = new RegExp(`^\\d{${digits}}$`);
    return !(!dni || !regex.test(dni));
  }

  validateRfcPF(rfc: string): boolean {
    const regex = /^[A-ZÑ]{4}[0-9]{6}[A-Za-z0-9ñÑ]{3}$/;
    if (!rfc || !regex.test(rfc)) {
      return false;
    }
    return true;
  }

  validateRfcPM(rfc: string): boolean {
    const regex = /^[A-ZÑ]{3}[0-9]{6}[A-Za-z0-9ñÑ]{3}$/;
    if (!rfc || !regex.test(rfc)) {
      return false;
    }
    return true;
  }

  validateCpf(cpf: string): boolean {
    const regex = /^\d{3}\.\d{3}\.\d{3}-[0-9A-Za-zñÑ]{2}$/;
    if (!cpf || !regex.test(cpf)) {
      return false;
    }
    return true;
  }

  /**
   * Validador para coexistencia permanente (Numérico y Alfanumérico).
   * Ref: Instrucción Normativa RFB nº 2229 (Brasil).
   */
  validateCnpj(cnpj: string): boolean {
    if (!cnpj) return false;

    // Normalizar: pasar a mayúsculas y remover caracteres especiales (puntos, guiones, barras)
    const normalized = cnpj.toUpperCase().replace(/[^A-Z0-9]/g, '');

    // Validar que tenga exactamente 14 caracteres alfanuméricos
    if (normalized.length !== 14 || !/^[A-Z0-9]{14}$/.test(normalized)) {
      return false;
    }

    const root = normalized.substring(0, 12);
    const dv = normalized.substring(12);

    // Los dos últimos dígitos verificadores (DV) siempre deben ser numéricos
    if (!/^\d{2}$/.test(dv)) {
      return false;
    }

    // Si la raíz contiene al menos una letra, se procesa bajo la regla alfanumérica
    const isAlphanumeric = !/^\d{12}$/.test(root);

    const expectedDv1 = this.calculateCnpjDv(
      root,
      this.WEIGHTS_DV1,
      isAlphanumeric
    );
    const expectedDv2 = this.calculateCnpjDv(
      root + expectedDv1,
      this.WEIGHTS_DV2,
      isAlphanumeric
    );

    return normalized === root + expectedDv1 + expectedDv2;
  }

  private calculateCnpjDv(
    input: string,
    weights: number[],
    asciiRule: boolean
  ): number {
    let sum = 0;

    for (let i = 0; i < weights.length; i++) {
      const charCode = input.charCodeAt(i);
      let value: number;

      if (asciiRule) {
        // Regla Alfanumérica RFB: Código ASCII menos 48
        // '0'-'9' (ASCII 48-57) -> 0-9
        // 'A'-'Z' (ASCII 65-90) -> 17-42
        value = charCode - 48;
      } else {
        // Regla Numérica tradicional
        value = parseInt(input.charAt(i), 10);
      }

      sum += value * weights[i];
    }

    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  }
}
